/**
 * デモデータフォールバックサービス
 * Supabase未接続時やデータが空の場合のフォールバックデータを提供
 */

import {
  LandTransporter,
  Client,
  Case,
  BusinessPartner,
  BusinessPartnerCategory,
  BusinessPartnerDataSource,
  BusinessPartnerStatus,
  BusinessPartnerRole,
  BusinessPartnerType,
} from '../types';

/**
 * デモ用フォールバックデータ
 */
export class DemoDataFallbackService {
  /**
   * デモ用取引先データ
   */
  public static getBusinessPartners(): BusinessPartner[] {
    return [
      {
        id: 'demo-bp-1',
        name: 'デモ自動車販売株式会社',
        category: BusinessPartnerCategory.DEALERSHIP,
        dataSource: BusinessPartnerDataSource.CUSTOM,
        displayName: 'デモ自動車販売',
        type: BusinessPartnerType.CORPORATE,
        status: BusinessPartnerStatus.ACTIVE,
        availableRoles: [
          BusinessPartnerRole.BILLING,
          BusinessPartnerRole.PICKUP,
          BusinessPartnerRole.DELIVERY,
        ],
        defaultAddress: '東京都渋谷区神南1-12-16',
        defaultPhone: '03-5555-1111',
        defaultContactPerson: '営業部 田中',
        defaultEmail: 'demo@demo-cars.co.jp',
        createdAt: '2025-01-01T00:00:00Z',
        updatedAt: '2025-01-01T00:00:00Z',
        notes: 'デモ用サンプル販売店です。各種車両を取り扱っています。',
        isSystemPreset: false,
        locations: [
          {
            id: 'demo-loc-1',
            name: '本店',
            address: '東京都渋谷区神南1-12-16',
            phone: '03-5555-1111',
            contactPerson: '営業部 田中',
            roles: [BusinessPartnerRole.PICKUP, BusinessPartnerRole.DELIVERY],
            isDefault: true,
          },
        ],
      },
      {
        id: 'demo-bp-2',
        name: 'デモオークション会場',
        category: BusinessPartnerCategory.AUCTION,
        dataSource: BusinessPartnerDataSource.CUSTOM,
        displayName: 'デモオークション',
        type: BusinessPartnerType.CORPORATE,
        status: BusinessPartnerStatus.ACTIVE,
        availableRoles: [BusinessPartnerRole.PICKUP, BusinessPartnerRole.DELIVERY],
        defaultAddress: '千葉県千葉市美浜区幸町2-24-1',
        defaultPhone: '043-2222-3333',
        defaultContactPerson: '運営事務局',
        defaultEmail: 'info@demo-auction.co.jp',
        createdAt: '2025-01-01T00:00:00Z',
        updatedAt: '2025-01-01T00:00:00Z',
        notes: 'デモ用オークション会場。月曜・木曜開催。',
        isSystemPreset: false,
        locations: [
          {
            id: 'demo-loc-2',
            name: 'メイン会場',
            address: '千葉県千葉市美浜区幸町2-24-1',
            phone: '043-2222-3333',
            contactPerson: '運営事務局',
            roles: [BusinessPartnerRole.PICKUP, BusinessPartnerRole.DELIVERY],
            isDefault: true,
          },
        ],
      },
      {
        id: 'demo-bp-3',
        name: 'デモ整備工場',
        category: BusinessPartnerCategory.OTHER,
        dataSource: BusinessPartnerDataSource.CUSTOM,
        displayName: 'デモ整備工場',
        type: BusinessPartnerType.CORPORATE,
        status: BusinessPartnerStatus.ACTIVE,
        availableRoles: [BusinessPartnerRole.PICKUP, BusinessPartnerRole.DELIVERY],
        defaultAddress: '神奈川県横浜市港北区新横浜2-5-10',
        defaultPhone: '045-4444-5555',
        defaultContactPerson: '工場長',
        defaultEmail: 'service@demo-maintenance.co.jp',
        createdAt: '2025-01-01T00:00:00Z',
        updatedAt: '2025-01-01T00:00:00Z',
        notes: 'デモ用整備工場。各種修理・点検対応。',
        isSystemPreset: false,
        locations: [
          {
            id: 'demo-loc-3',
            name: '工場',
            address: '神奈川県横浜市港北区新横浜2-5-10',
            phone: '045-4444-5555',
            contactPerson: '工場長',
            roles: [BusinessPartnerRole.PICKUP, BusinessPartnerRole.DELIVERY],
            isDefault: true,
          },
        ],
      },
    ];
  }

  /**
   * デモ用陸送員データ
   */
  public static getLandTransporters(): LandTransporter[] {
    return [
      {
        id: 'demo-driver-1',
        name: '田中 陸送員',
        phone: '090-1111-1111',
        address: '東京都新宿区',
        status: 'Available',
        drivableVehicleTypes: ['小型車', '普通車'],
        workingDays: {
          monday: { startTime: '08:00', endTime: '18:00' },
          tuesday: { startTime: '08:00', endTime: '18:00' },
          wednesday: { startTime: '08:00', endTime: '18:00' },
          thursday: { startTime: '08:00', endTime: '18:00' },
          friday: { startTime: '08:00', endTime: '18:00' },
          saturday: { startTime: '08:00', endTime: '18:00' },
          sunday: null,
        },
        hasLicenseForLargeVehicles: false,
        canWorkNightShift: true,
        canWorkLongDistance: true,
        hasTemporaryLicense: false,
        isATOnly: false,
        isSolicitationProhibited: false,
        isDesignatedStoreProhibited: false,
      },
      {
        id: 'demo-driver-2',
        name: '佐藤 運送',
        phone: '090-2222-2222',
        address: '神奈川県横浜市',
        status: 'Available',
        drivableVehicleTypes: ['小型車', '普通車', '大型車'],
        workingDays: {
          monday: { startTime: '08:00', endTime: '18:00' },
          tuesday: { startTime: '08:00', endTime: '18:00' },
          wednesday: { startTime: '08:00', endTime: '18:00' },
          thursday: { startTime: '08:00', endTime: '18:00' },
          friday: { startTime: '08:00', endTime: '18:00' },
          saturday: null,
          sunday: null,
        },
        hasLicenseForLargeVehicles: true,
        canWorkNightShift: false,
        canWorkLongDistance: true,
        hasTemporaryLicense: true,
        isATOnly: false,
        isSolicitationProhibited: false,
        isDesignatedStoreProhibited: false,
      },
      {
        id: 'demo-driver-3',
        name: '鈴木 配送',
        phone: '090-3333-3333',
        address: '埼玉県さいたま市',
        status: 'Busy',
        drivableVehicleTypes: ['普通車'],
        workingDays: {
          monday: { startTime: '08:00', endTime: '18:00' },
          tuesday: { startTime: '08:00', endTime: '18:00' },
          wednesday: null,
          thursday: { startTime: '08:00', endTime: '18:00' },
          friday: { startTime: '08:00', endTime: '18:00' },
          saturday: { startTime: '08:00', endTime: '18:00' },
          sunday: { startTime: '08:00', endTime: '18:00' },
        },
        hasLicenseForLargeVehicles: false,
        canWorkNightShift: true,
        canWorkLongDistance: false,
        hasTemporaryLicense: false,
        isATOnly: true,
        isSolicitationProhibited: false,
        isDesignatedStoreProhibited: false,
      },
    ];
  }

  /**
   * デモ用案件データ
   */
  public static getCases(): Case[] {
    return [
      {
        id: 'DEMO-2025-0001',
        caseNumber: 'C000001',
        pickupDate: '2025-07-12',
        pickupTimeStart: '10:00',
        pickupTimeEnd: '12:00',
        pickupReport: null,
        deliveryDate: '2025-07-12',
        deliveryTime: '15:00',
        deliveryReport: null,
        vehicleModel: 'demo-vehicle-1',
        vehicleType: 'セダン',
        vehiclePlateNumber: 'デモ123',
        pickupPartnerId: 'demo-bp-2',
        pickupLocationId: 'demo-loc-2',
        deliveryPartnerId: 'demo-bp-1',
        deliveryLocationId: 'demo-loc-1',
        billingPartnerId: 'demo-bp-1',
        billingLocationId: 'demo-loc-1',
        status: 'assigned',
        assignedLandTransporterId: 'demo-driver-1',
        transportFee: 30000,
        pickupAddress: '千葉県千葉市美浜区幸町2-24-1',
        pickupPhone: '043-2222-3333',
        deliveryAddress: '東京都渋谷区神南1-12-16',
        deliveryPhone: '03-5555-1111',
        // 後方互換性
        billingClientId: '',
        pickupClientId: '',
        deliveryClientId: '',
        price: 30000,
        remarks: '',
      },
      {
        id: 'DEMO-2025-0002',
        caseNumber: 'C000002',
        pickupDate: '2025-07-13',
        pickupTimeStart: '09:00',
        pickupTimeEnd: '11:00',
        pickupReport: null,
        deliveryDate: '2025-07-13',
        deliveryTime: '16:30',
        deliveryReport: null,
        vehicleModel: 'demo-vehicle-2',
        vehicleType: 'SUV',
        vehiclePlateNumber: 'デモ456',
        pickupPartnerId: 'demo-bp-1',
        pickupLocationId: 'demo-loc-1',
        deliveryPartnerId: 'demo-bp-3',
        deliveryLocationId: 'demo-loc-3',
        billingPartnerId: 'demo-bp-1',
        billingLocationId: 'demo-loc-1',
        status: 'in_transit',
        assignedLandTransporterId: 'demo-driver-2',
        transportFee: 25000,
        pickupAddress: '東京都渋谷区神南1-12-16',
        pickupPhone: '03-5555-1111',
        deliveryAddress: '神奈川県横浜市港北区新横浜2-5-10',
        deliveryPhone: '045-4444-5555',
        // 後方互換性
        billingClientId: '',
        pickupClientId: '',
        deliveryClientId: '',
        price: 30000,
        remarks: '',
      },
      {
        id: 'DEMO-2025-0003',
        caseNumber: 'C000003',
        pickupDate: '2025-07-14',
        pickupTimeStart: '13:00',
        pickupTimeEnd: '15:00',
        pickupReport: null,
        deliveryDate: '2025-07-14',
        deliveryTime: '18:00',
        deliveryReport: null,
        vehicleModel: 'demo-vehicle-3',
        vehicleType: 'ミニバン',
        vehiclePlateNumber: 'デモ789',
        pickupPartnerId: 'demo-bp-3',
        pickupLocationId: 'demo-loc-3',
        deliveryPartnerId: 'demo-bp-1',
        deliveryLocationId: 'demo-loc-1',
        billingPartnerId: 'demo-bp-1',
        billingLocationId: 'demo-loc-1',
        status: 'provisional',
        transportFee: 35000,
        pickupAddress: '神奈川県横浜市港北区新横浜2-5-10',
        pickupPhone: '045-4444-5555',
        deliveryAddress: '東京都渋谷区神南1-12-16',
        deliveryPhone: '03-5555-1111',
        // 後方互換性
        billingClientId: '',
        pickupClientId: '',
        deliveryClientId: '',
        price: 30000,
        remarks: '',
      },
    ];
  }

  /**
   * デモ用顧客データ（後方互換性のため）
   */
  public static getClients(): Client[] {
    return [
      {
        id: 'demo-client-1',
        companyName: 'デモ自動車販売株式会社',
        contactPerson: '営業部 田中',
        phone: '03-5555-1111',
        email: 'demo@demo-cars.co.jp',
        address: '東京都渋谷区神南1-12-16',
        status: 'Active',
      },
      {
        id: 'demo-client-2',
        companyName: 'デモオークション会場',
        contactPerson: '運営事務局',
        phone: '043-2222-3333',
        email: 'info@demo-auction.co.jp',
        address: '千葉県千葉市美浜区幸町2-24-1',
        status: 'Active',
      },
    ];
  }
}
